import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PrivacyBadge from "../components/PrivacyBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const STATUS_STYLES = {
    open: "bg-blue-50 text-blue-700 border-blue-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-slate-100 text-slate-600 border-slate-200",
};

function Stat({ label, value, testId }) {
    return (
        <div className="border border-slate-200 bg-white rounded-lg p-4" data-testid={testId}>
            <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${STATUS_STYLES[status] || STATUS_STYLES.open}`}>
            {status?.replace("_", " ")}
        </span>
    );
}

export default function AdminTriage() {
    const [data, setData] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [manageOpen, setManageOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [escalationOpen, setEscalationOpen] = useState(false);
    const [actionOpen, setActionOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [escalationCase, setEscalationCase] = useState(null);
    const [actionCase, setActionCase] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [manageForm, setManageForm] = useState({
        assignee_id: "",
        priority: "normal",
        due_date: "",
        next_chase_date: "",
        management_notes: "",
    });
    const [bulkForm, setBulkForm] = useState({
        assignee_id: "",
        priority: "normal",
        due_date: "",
        next_chase_date: "",
        management_notes: "",
    });
    const [escalationForm, setEscalationForm] = useState({ template: "agency_chase", visibility: "admin_only" });
    const [actionForm, setActionForm] = useState({
        action_type: "agency_response",
        visibility: "admin_only",
        body: "",
        status: "",
        due_date: "",
        next_chase_date: "",
        response_source: "",
        reference: "",
    });
    const [filters, setFilters] = useState({
        category: "all",
        status: "all",
        visibility: "all",
        reported: "all",
    });

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.category !== "all") params.category = filters.category;
            if (filters.status !== "all") params.status = filters.status;
            if (filters.visibility !== "all") params.visibility = filters.visibility;
            if (filters.reported !== "all") params.reported = filters.reported === "true";
            const [triageRes, usersRes] = await Promise.all([
                api.get("/admin/triage", { params }),
                api.get("/admin/users"),
            ]);
            setData(triageRes.data);
            setSelectedIds((ids) => ids.filter((id) => (triageRes.data.items || []).some((item) => item.id === id)));
            setUsers((usersRes.data || []).filter((u) => u.role === "estate_admin"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load triage queue");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.category, filters.status, filters.visibility, filters.reported]);

    const updateStatus = async (caseId, status) => {
        try {
            await api.patch(`/cases/${caseId}`, { status });
            toast.success("Case status updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update status");
        }
    };

    const openManage = (item) => {
        setSelected(item);
        setManageForm({
            assignee_id: item.assignee_id || "",
            priority: item.priority || "normal",
            due_date: item.due_date || "",
            next_chase_date: item.next_chase_date || "",
            management_notes: item.management_notes || "",
        });
        setManageOpen(true);
    };

    const saveManage = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/admin/triage/${selected.id}`, {
                ...manageForm,
                assignee_id: manageForm.assignee_id || "",
                due_date: manageForm.due_date || null,
                next_chase_date: manageForm.next_chase_date || null,
                management_notes: manageForm.management_notes || null,
            });
            toast.success("Triage details updated");
            setManageOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update triage details");
        }
    };

    const toggleSelected = (caseId) => {
        setSelectedIds((ids) => ids.includes(caseId) ? ids.filter((id) => id !== caseId) : [...ids, caseId]);
    };

    const toggleAll = () => {
        setSelectedIds((ids) => ids.length === items.length ? [] : items.map((item) => item.id));
    };

    const saveBulk = async (e) => {
        e.preventDefault();
        try {
            const res = await api.post("/admin/triage/bulk", {
                case_ids: selectedIds,
                ...bulkForm,
                assignee_id: bulkForm.assignee_id || "",
                due_date: bulkForm.due_date || null,
                next_chase_date: bulkForm.next_chase_date || null,
                management_notes: bulkForm.management_notes || null,
            });
            toast.success(`Updated ${res.data.updated} case(s)`);
            setBulkOpen(false);
            setSelectedIds([]);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not bulk update triage details");
        }
    };

    const openEscalation = (item) => {
        setEscalationCase(item);
        setEscalationForm({
            template: item.category === "parking" ? "parking_enforcement" : item.category === "safety" ? "safety_follow_up" : item.category === "maintenance" ? "maintenance_follow_up" : "agency_chase",
            visibility: item.visibility === "agency_shared" ? "agency_shared" : "admin_only",
        });
        setEscalationOpen(true);
    };

    const saveEscalation = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/admin/triage/${escalationCase.id}/escalation-template`, escalationForm);
            toast.success("Escalation note added");
            setEscalationOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add escalation note");
        }
    };

    const openAction = (item) => {
        const actionType = item.visibility === "agency_shared" ? "agency_response" : "management_response";
        setActionCase(item);
        setActionForm({
            action_type: actionType,
            visibility: item.visibility === "agency_shared" ? "agency_shared" : "admin_only",
            body: "",
            status: "",
            due_date: item.due_date || "",
            next_chase_date: item.next_chase_date || "",
            response_source: "",
            reference: "",
        });
        setActionOpen(true);
    };

    const saveAction = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/admin/triage/${actionCase.id}/management-action`, {
                ...actionForm,
                status: actionForm.status || null,
                due_date: actionForm.due_date || null,
                next_chase_date: actionForm.next_chase_date || null,
                response_source: actionForm.response_source || null,
                reference: actionForm.reference || null,
            });
            toast.success("Management action recorded");
            setActionOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not record management action");
        }
    };

    const managementVisibilityOptions = (item) => {
        const options = [
            { value: "admin_only", label: "Admin only" },
            { value: "private", label: "Private to owner/admin" },
        ];
        if (["household", "estate_shared"].includes(item?.visibility)) {
            options.push({ value: "household", label: "Household" });
        }
        if (item?.visibility === "estate_shared") {
            options.push({ value: "estate_shared", label: "Estate-shared" });
        }
        if (item?.visibility === "agency_shared") {
            options.push({ value: "agency_shared", label: "Agency shared" });
        }
        return options;
    };

    const summary = data?.summary || {};
    const items = data?.items || [];

    return (
        <div className="space-y-6" data-testid="admin-triage-page">
            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Admin</div>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Management triage</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Review stale issues, agency-shared cases, reported PCNs, and safety items from one queue.
                </p>
            </div>

            <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
                <Stat label="Total" value={summary.total || 0} testId="triage-stat-total" />
                <Stat label="Open" value={summary.open || 0} testId="triage-stat-open" />
                <Stat label="In progress" value={summary.in_progress || 0} testId="triage-stat-progress" />
                <Stat label="Stale" value={summary.stale || 0} testId="triage-stat-stale" />
                <Stat label="Agency shared" value={summary.agency_shared || 0} testId="triage-stat-agency" />
                <Stat label="Safety open" value={summary.safety_open || 0} testId="triage-stat-safety" />
                <Stat label="Due today" value={summary.due_today || 0} testId="triage-stat-due-today" />
                <Stat label="Overdue" value={summary.due_overdue || 0} testId="triage-stat-due-overdue" />
                <Stat label="Chase due" value={summary.chase_due || 0} testId="triage-stat-chase-due" />
            </section>

            <section className="border border-slate-200 bg-white rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                        <SelectTrigger data-testid="triage-category-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            <SelectItem value="parking">Parking</SelectItem>
                            <SelectItem value="maintenance">Maintenance</SelectItem>
                            <SelectItem value="safety">Safety</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                        <SelectTrigger data-testid="triage-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="in_progress">In progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filters.visibility} onValueChange={(v) => setFilters({ ...filters, visibility: v })}>
                        <SelectTrigger data-testid="triage-visibility-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All visibility</SelectItem>
                            <SelectItem value="private">Private</SelectItem>
                            <SelectItem value="household">Household</SelectItem>
                            <SelectItem value="estate_shared">Estate-shared</SelectItem>
                            <SelectItem value="agency_shared">Agency-shared</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filters.reported} onValueChange={(v) => setFilters({ ...filters, reported: v })}>
                        <SelectTrigger data-testid="triage-reported-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Reported and unreported</SelectItem>
                            <SelectItem value="true">Reported only</SelectItem>
                            <SelectItem value="false">Unreported only</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </section>

            <section className="border border-slate-200 bg-white rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="triage-bulk-toolbar">
                <div className="text-sm text-slate-600">
                    {selectedIds.length} selected
                </div>
                <Button
                    variant="outline"
                    disabled={selectedIds.length === 0}
                    onClick={() => setBulkOpen(true)}
                    data-testid="triage-bulk-open-button"
                >
                    Bulk manage
                </Button>
            </section>

            <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="triage-table">
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="table-header-cell">
                                <input
                                    type="checkbox"
                                    checked={items.length > 0 && selectedIds.length === items.length}
                                    onChange={toggleAll}
                                    aria-label="Select all cases"
                                    data-testid="triage-select-all"
                                />
                            </th>
                            <th className="table-header-cell">Case</th>
                            <th className="table-header-cell">Property</th>
                            <th className="table-header-cell">Status</th>
                            <th className="table-header-cell">Visibility</th>
                            <th className="table-header-cell">Owner</th>
                            <th className="table-header-cell">Age</th>
                            <th className="table-header-cell">Evidence</th>
                            <th className="table-header-cell">Action</th>
                            <th className="table-header-cell"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={10} className="table-cell-tight text-slate-500">Loading...</td></tr>}
                        {!loading && items.length === 0 && <tr><td colSpan={10} className="table-cell-tight text-slate-500">No cases match these filters.</td></tr>}
                        {items.map((item) => (
                            <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`triage-row-${item.id}`}>
                                <td className="table-cell-tight">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(item.id)}
                                        onChange={() => toggleSelected(item.id)}
                                        aria-label={`Select ${item.title}`}
                                        data-testid={`triage-select-${item.id}`}
                                    />
                                </td>
                                <td className="table-cell-tight">
                                    <div className="font-medium text-slate-900">{item.title}</div>
                                    <div className="text-xs text-slate-500 capitalize">{item.category}</div>
                                </td>
                                <td className="table-cell-tight text-slate-700">
                                    {item.property_unit || "-"}
                                    <div className="text-xs text-slate-500">{item.property_address || ""}</div>
                                </td>
                                <td className="table-cell-tight">
                                    <div className="space-y-2">
                                        <StatusBadge status={item.status} />
                                        <Select value={item.status} onValueChange={(v) => updateStatus(item.id, v)}>
                                            <SelectTrigger className="w-36 h-8" data-testid={`triage-status-${item.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="open">Open</SelectItem>
                                                <SelectItem value="in_progress">In progress</SelectItem>
                                                <SelectItem value="resolved">Resolved</SelectItem>
                                                <SelectItem value="closed">Closed</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </td>
                                <td className="table-cell-tight"><PrivacyBadge visibility={item.visibility} /></td>
                                <td className="table-cell-tight text-slate-700">
                                    {item.assignee_email || "-"}
                                    <div className="text-xs text-slate-500 capitalize">Priority: {item.priority || "normal"}</div>
                                    {item.due_date && <div className={`text-xs ${item.due_state === "overdue" ? "text-rose-700 font-medium" : item.due_state === "due_today" ? "text-amber-700 font-medium" : "text-slate-500"}`}>Due {item.due_date}</div>}
                                    {item.next_chase_date && <div className={`text-xs ${item.chase_state === "overdue" ? "text-rose-700 font-medium" : item.chase_state === "due_today" ? "text-amber-700 font-medium" : "text-slate-500"}`}>Chase {item.next_chase_date}</div>}
                                </td>
                                <td className="table-cell-tight text-slate-700">
                                    {item.age_days}d
                                    {item.is_stale && <div className="text-xs text-amber-700 font-medium">Stale</div>}
                                    {item.is_reported && <div className="text-xs text-slate-500">Reported</div>}
                                </td>
                                <td className="table-cell-tight text-slate-700">
                                    {item.document_count} doc(s)
                                    <div className="text-xs text-slate-500">{item.appeal_draft_count} draft(s)</div>
                                </td>
                                <td className="table-cell-tight text-slate-700">
                                    {item.action_needed}
                                    {item.latest_event && (
                                        <div className="text-xs text-slate-500 mt-1">
                                            Last: {item.latest_event.event_type?.replace("_", " ")}
                                        </div>
                                    )}
                                </td>
                                <td className="table-cell-tight">
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => openManage(item)} data-testid={`manage-triage-${item.id}`}>
                                            Manage
                                        </Button>
                                        <Button asChild variant="outline" size="sm" data-testid={`open-triage-case-${item.id}`}>
                                            <Link to={`/cases/${item.id}`}>Open</Link>
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => openEscalation(item)} data-testid={`triage-escalate-${item.id}`}>
                                            Escalate
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => openAction(item)} data-testid={`triage-action-${item.id}`}>
                                            Record action
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <Dialog open={manageOpen} onOpenChange={setManageOpen}>
                <DialogContent data-testid="triage-manage-dialog">
                    <DialogHeader>
                        <DialogTitle>Manage triage</DialogTitle>
                        <DialogDescription>
                            Assign ownership, priority, due dates and chase dates for this management item.
                        </DialogDescription>
                    </DialogHeader>
                    {selected && (
                        <form onSubmit={saveManage} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label>Assignee</Label>
                                <Select value={manageForm.assignee_id || "none"} onValueChange={(v) => setManageForm({ ...manageForm, assignee_id: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="triage-assignee-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Unassigned</SelectItem>
                                        {users.map((u) => (
                                            <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Priority</Label>
                                    <Select value={manageForm.priority} onValueChange={(v) => setManageForm({ ...manageForm, priority: v })}>
                                        <SelectTrigger data-testid="triage-priority-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="normal">Normal</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                            <SelectItem value="urgent">Urgent</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Due date</Label>
                                    <Input type="date" value={manageForm.due_date || ""} onChange={(e) => setManageForm({ ...manageForm, due_date: e.target.value })} data-testid="triage-due-date-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Next chase</Label>
                                    <Input type="date" value={manageForm.next_chase_date || ""} onChange={(e) => setManageForm({ ...manageForm, next_chase_date: e.target.value })} data-testid="triage-next-chase-input" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Management notes</Label>
                                <Textarea rows={4} value={manageForm.management_notes || ""} onChange={(e) => setManageForm({ ...manageForm, management_notes: e.target.value })} data-testid="triage-management-notes-input" />
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="triage-save-management-button">
                                    Save management details
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent data-testid="triage-bulk-dialog">
                    <DialogHeader>
                        <DialogTitle>Bulk manage triage</DialogTitle>
                        <DialogDescription>
                            Apply assignment, priority, dates, and notes to {selectedIds.length} selected case(s).
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveBulk} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label>Assignee</Label>
                            <Select value={bulkForm.assignee_id || "none"} onValueChange={(v) => setBulkForm({ ...bulkForm, assignee_id: v === "none" ? "" : v })}>
                                <SelectTrigger data-testid="triage-bulk-assignee-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Unassigned</SelectItem>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.email})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="space-y-1.5">
                                <Label>Priority</Label>
                                <Select value={bulkForm.priority} onValueChange={(v) => setBulkForm({ ...bulkForm, priority: v })}>
                                    <SelectTrigger data-testid="triage-bulk-priority-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Low</SelectItem>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="high">High</SelectItem>
                                        <SelectItem value="urgent">Urgent</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Due date</Label>
                                <Input type="date" value={bulkForm.due_date || ""} onChange={(e) => setBulkForm({ ...bulkForm, due_date: e.target.value })} data-testid="triage-bulk-due-date-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Next chase</Label>
                                <Input type="date" value={bulkForm.next_chase_date || ""} onChange={(e) => setBulkForm({ ...bulkForm, next_chase_date: e.target.value })} data-testid="triage-bulk-next-chase-input" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Management notes</Label>
                            <Textarea rows={4} value={bulkForm.management_notes || ""} onChange={(e) => setBulkForm({ ...bulkForm, management_notes: e.target.value })} data-testid="triage-bulk-management-notes-input" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="triage-bulk-submit-button">
                                Apply to selected cases
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={escalationOpen} onOpenChange={setEscalationOpen}>
                <DialogContent data-testid="triage-escalation-dialog">
                    <DialogHeader>
                        <DialogTitle>Add escalation note</DialogTitle>
                        <DialogDescription>
                            Create a standard follow-up note on the case timeline. Agency-visible notes require the case to already be agency-shared.
                        </DialogDescription>
                    </DialogHeader>
                    {escalationCase && (
                        <form onSubmit={saveEscalation} className="space-y-4">
                            <div className="border border-slate-200 rounded-md p-3 text-sm text-slate-700">
                                {escalationCase.title}
                            </div>
                            <div className="space-y-1.5">
                                <Label>Template</Label>
                                <Select value={escalationForm.template} onValueChange={(v) => setEscalationForm({ ...escalationForm, template: v })}>
                                    <SelectTrigger data-testid="triage-escalation-template-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="agency_chase">Agency chase</SelectItem>
                                        <SelectItem value="parking_enforcement">Parking enforcement review</SelectItem>
                                        <SelectItem value="safety_follow_up">Safety follow-up</SelectItem>
                                        <SelectItem value="maintenance_follow_up">Maintenance follow-up</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Visibility</Label>
                                <Select value={escalationForm.visibility} onValueChange={(v) => setEscalationForm({ ...escalationForm, visibility: v })}>
                                    <SelectTrigger data-testid="triage-escalation-visibility-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin_only">Admin only</SelectItem>
                                        {escalationCase.visibility === "agency_shared" && <SelectItem value="agency_shared">Agency shared</SelectItem>}
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="triage-escalation-submit-button">
                                    Add escalation note
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={actionOpen} onOpenChange={setActionOpen}>
                <DialogContent data-testid="triage-action-dialog">
                    <DialogHeader>
                        <DialogTitle>Record management action</DialogTitle>
                        <DialogDescription>
                            Capture agency responses, chases, escalations, and resolution decisions as structured case history.
                        </DialogDescription>
                    </DialogHeader>
                    {actionCase && (
                        <form onSubmit={saveAction} className="space-y-4">
                            <div className="border border-slate-200 rounded-md p-3 text-sm text-slate-700">
                                {actionCase.title}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Action type</Label>
                                    <Select value={actionForm.action_type} onValueChange={(v) => setActionForm({ ...actionForm, action_type: v })}>
                                        <SelectTrigger data-testid="triage-action-type-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="agency_response">Agency response</SelectItem>
                                            <SelectItem value="agency_chase">Agency chase</SelectItem>
                                            <SelectItem value="escalation">Escalation</SelectItem>
                                            <SelectItem value="management_response">Management response</SelectItem>
                                            <SelectItem value="resolution">Resolution</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Visibility</Label>
                                    <Select value={actionForm.visibility} onValueChange={(v) => setActionForm({ ...actionForm, visibility: v })}>
                                        <SelectTrigger data-testid="triage-action-visibility-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {managementVisibilityOptions(actionCase).map((option) => (
                                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Action note</Label>
                                <Textarea
                                    rows={4}
                                    value={actionForm.body}
                                    onChange={(e) => setActionForm({ ...actionForm, body: e.target.value })}
                                    placeholder="Paste the agency response, record the chase sent, or explain the management decision."
                                    required
                                    data-testid="triage-action-body-input"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Set status</Label>
                                    <Select value={actionForm.status || "unchanged"} onValueChange={(v) => setActionForm({ ...actionForm, status: v === "unchanged" ? "" : v })}>
                                        <SelectTrigger data-testid="triage-action-status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="unchanged">Leave unchanged</SelectItem>
                                            <SelectItem value="open">Open</SelectItem>
                                            <SelectItem value="in_progress">In progress</SelectItem>
                                            <SelectItem value="resolved">Resolved</SelectItem>
                                            <SelectItem value="closed">Closed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Due date</Label>
                                    <Input type="date" value={actionForm.due_date || ""} onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })} data-testid="triage-action-due-date-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Next chase</Label>
                                    <Input type="date" value={actionForm.next_chase_date || ""} onChange={(e) => setActionForm({ ...actionForm, next_chase_date: e.target.value })} data-testid="triage-action-next-chase-input" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Source</Label>
                                    <Input value={actionForm.response_source || ""} onChange={(e) => setActionForm({ ...actionForm, response_source: e.target.value })} placeholder="Agency email, call, portal, resident update" data-testid="triage-action-source-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Reference</Label>
                                    <Input value={actionForm.reference || ""} onChange={(e) => setActionForm({ ...actionForm, reference: e.target.value })} placeholder="Ticket, PCN, contractor or email reference" data-testid="triage-action-reference-input" />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="triage-action-submit-button">
                                    Record action
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
